/**
 * Roster Gallery — public Pinterest-grid of every guest-uploaded photo
 * for one wedding-party member at /site/:slug/party/:guestId/photos.
 * Click → lightbox → "Share this moment" generates a Get-Locked-In-style
 * "X partied with Y" PNG via the photo-card endpoint.
 */
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, X, Share2, Download } from "lucide-react";
import axios from "axios";
import { exportBoth } from "../components/NewsShare";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function RosterGalleryPage() {
  const { slug, guestId } = useParams();
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null);  // currently-viewed photo
  const [caption, setCaption] = useState("");

  async function load() {
    try {
      const { data: g } = await axios.get(
        `${BACKEND}/api/public/site/${slug}/party/${guestId}/gallery`,
      );
      setData(g);
    } catch {
      setData(false);
    }
  }
  useEffect(() => { load(); }, [slug, guestId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function shareMoment(messageId, layout) {
    const url = `${BACKEND}/api/public/site/${slug}/party/${guestId}/photo-card.png?message_id=${messageId}&layout=${layout}${caption ? `&headline=${encodeURIComponent(caption)}` : ""}`;
    const m = data?.photos?.find((p) => p.message_id === messageId);
    try { await exportBoth(url, `${BACKEND}${m?.photo_url}`, `moment-${messageId}`); }
    catch { toast.error("Could not generate share card"); }
  }

  if (data === false) {
    return <div className="min-h-screen flex flex-col items-center justify-center gap-3 text-sm text-[var(--muted)]" data-testid="roster-gallery-error">This gallery isn't available.<a href={`/site/${slug}`} className="underline text-[var(--gold)]">Back to the wedding site</a></div>;
  }
  if (!data) {
    return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-[var(--gold)] border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="min-h-screen bg-[var(--bg)]" data-testid="roster-gallery-page">
      <header className="bg-[var(--ink)] text-white py-12 px-6">
        <div className="max-w-5xl mx-auto">
          <Link to={`/site/${slug}/party/${guestId}`} className="text-xs text-white/60 hover:text-[var(--gold)] flex items-center gap-1 mb-4">
            <ArrowLeft className="w-3 h-3" /> Back to {data.member_name?.split(" ")[0]}'s page
          </Link>
          <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-2">Roster Gallery</p>
          <h1 className="font-serif font-light text-4xl md:text-5xl">Moments with {data.member_name}</h1>
          <p className="text-white/70 text-sm mt-3">{data.photos.length} {data.photos.length === 1 ? "photo" : "photos"} from friends and family</p>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-6 py-10">
        {data.photos.length === 0 ? (
          <div className="text-center py-20 text-[var(--muted)]">
            <p className="font-serif text-2xl mb-2">No photos yet</p>
            <p className="text-sm mb-6">Be the first to drop a memory.</p>
            <Link to={`/site/${slug}/party/${guestId}`} className="btn-gold">Add a photo →</Link>
          </div>
        ) : (
          <div className="columns-2 md:columns-3 lg:columns-4 gap-3 [column-fill:_balance]">
            {data.photos.map((p) => (
              <button
                key={p.message_id}
                onClick={() => setActive(p)}
                data-testid={`gallery-photo-${p.message_id}`}
                className="relative w-full mb-3 break-inside-avoid bg-white rounded-xl overflow-hidden border border-[var(--line)] hover:border-[var(--gold)] transition group"
              >
                <img
                  src={`${BACKEND}${p.photo_url}`}
                  alt={`${p.from_name} with ${data.member_name}`}
                  className="w-full block group-hover:scale-105 transition duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-3 opacity-0 group-hover:opacity-100 transition">
                  <p className="text-white text-xs font-medium">{p.from_name}</p>
                  {p.message && <p className="text-white/80 text-[10px] line-clamp-2 mt-0.5">{p.message}</p>}
                </div>
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Lightbox */}
      {active && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setActive(null)}
          data-testid="gallery-lightbox"
        >
          <button
            onClick={() => setActive(null)}
            data-testid="lightbox-close"
            className="absolute top-4 right-4 text-white/80 hover:text-white"
          >
            <X className="w-6 h-6" />
          </button>
          <div onClick={(e) => e.stopPropagation()} className="max-w-3xl w-full">
            <img
              src={`${BACKEND}${active.photo_url}`}
              alt=""
              className="max-h-[70vh] w-full object-contain rounded-2xl"
            />
            <div className="bg-white rounded-2xl mt-3 p-5">
              <p className="text-[10px] uppercase tracking-[0.22em] text-[var(--gold)] mb-2">{active.from_name} & {data.member_name}</p>
              {active.message && <p className="text-[var(--ink)] mb-3">{active.message}</p>}
              <input value={caption} maxLength={120} onChange={(e) => setCaption(e.target.value)} placeholder="Add your breaking-news headline (optional)" className="input-luxe mb-3" data-testid="share-moment-caption" />
              <div className="flex gap-2 flex-wrap">
                <button onClick={() => shareMoment(active.message_id, "square")} data-testid="share-moment-square" className="btn-gold text-xs">
                  <Share2 className="w-3 h-3" /> Share moment · Feed
                </button>
                <button onClick={() => shareMoment(active.message_id, "story")} data-testid="share-moment-story" className="btn-outline-gold text-xs">
                  <Download className="w-3 h-3" /> Story card
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
